/*
 * Purpose: Persist AI-built + user connectors — tenant-scoped library
 * Last modified: 2026-06-28 | Agent
 * Completeness: 90
 */
import { create } from 'zustand';
import { BUILTIN_CONNECTORS, MCP_PRESET_CONNECTORS, getBuiltinConnector } from './builtins';
import { matchOutcomeSeed } from '@/seeds/averageOutcomes';
import type { AutoBlocksConnector } from './types';
import { connectorsStorageKey, migrateLegacyTenantStorageIfNeeded } from '@/lib/tenantStorage';

function loadCustom(): AutoBlocksConnector[] {
  try {
    migrateLegacyTenantStorageIfNeeded();
    const raw = localStorage.getItem(connectorsStorageKey());
    if (!raw) return [];
    return JSON.parse(raw) as AutoBlocksConnector[];
  } catch {
    return [];
  }
}

function persistCustom(custom: AutoBlocksConnector[]) {
  migrateLegacyTenantStorageIfNeeded();
  localStorage.setItem(connectorsStorageKey(), JSON.stringify(custom));
}

interface ConnectorLibraryStore {
  customConnectors: AutoBlocksConnector[];
  load: () => void;
  getAll: () => AutoBlocksConnector[];
  getById: (id: string) => AutoBlocksConnector | undefined;
  register: (connector: AutoBlocksConnector) => void;
  remove: (id: string) => void;
}

export const useConnectorLibraryStore = create<ConnectorLibraryStore>((set, get) => ({
  customConnectors: loadCustom(),

  load: () => set({ customConnectors: loadCustom() }),

  getAll: () => [...BUILTIN_CONNECTORS, ...MCP_PRESET_CONNECTORS, ...get().customConnectors],

  getById: (id) => getBuiltinConnector(id) ?? get().customConnectors.find((c) => c.id === id),

  register: (connector) => {
    set((state) => {
      const filtered = state.customConnectors.filter((c) => c.id !== connector.id);
      const customConnectors = [...filtered, connector];
      persistCustom(customConnectors);
      return { customConnectors };
    });
  },

  remove: (id) => {
    if (getBuiltinConnector(id)) return;
    set((state) => {
      const customConnectors = state.customConnectors.filter((c) => c.id !== id);
      persistCustom(customConnectors);
      return { customConnectors };
    });
  },
}));

export function listConnectorCatalogForPrompt(): string {
  return useConnectorLibraryStore
    .getState()
    .getAll()
    .map((c) => `${c.id} (${c.category}): ${c.description} [${c.capabilities.map((x) => x.id).join(', ')}]`)
    .join('\n');
}

export function findConnectorsForOutcome(text: string): AutoBlocksConnector[] {
  const seed = matchOutcomeSeed(text);
  if (seed) {
    return seed.expectedConnectorIds
      .map((id) => getBuiltinConnector(id))
      .filter((c): c is AutoBlocksConnector => Boolean(c));
  }

  const lower = text.toLowerCase();

  const mcpPresetHits = MCP_PRESET_CONNECTORS.filter((c) => {
    const nameWords = c.name.toLowerCase().replace(/[()]/g, '').split(/[\s/]+/).filter((w) => w.length > 2);
    return nameWords.some((word) => new RegExp(`\\b${word}\\b`, 'i').test(lower));
  });
  if (mcpPresetHits.length > 0) return mcpPresetHits;

  return useConnectorLibraryStore.getState().getAll().filter((c) => {
    const hay = `${c.name} ${c.category} ${c.description} ${c.capabilities.map((x) => x.label).join(' ')}`.toLowerCase();
    if (lower.includes('email') || lower.includes('gmail')) return c.id === 'ab.gmail' || hay.includes('email');
    if (/mcp|model context protocol|cursor mcp/i.test(lower)) return c.id === 'ab.mcp';
    if (lower.includes('slack')) return hay.includes('slack') || c.category === 'integration';
    if (/resume|cover|career|cv|ats/i.test(lower)) {
      return c.id === 'ab.custom.careeros' || c.id === 'ab.openai' || c.category === 'career' || hay.includes('resume');
    }
    if (/ai|gpt|openai/i.test(lower)) return c.id === 'ab.openai' || c.category === 'ai';
    if (/track|store|log|spreadsheet/i.test(lower)) return c.id === 'ab.storage';
    if (/notify|alert|remind/i.test(lower)) return c.id === 'ab.notify' || c.id === 'ab.scheduler';
    return false;
  });
}
